import localStorageRepositories from '@/plugins/localStorageRepositories';
import { showQuestionnaireModal } from '@/modules/setupQuestionnaireModal';
import { closeModal } from '@/helpers/closeModal';
import { refreshPreparedCampaignThumbnailDisplay } from '@/helpers/preparedCampaignDisplay';
import { activeVideoPlay } from '@/modules/actions/videoControls';
import { pauseAllProgressBars, resumeAllProgressBars } from '@/modules/actions/headerLineDuration.js';
import { pauseDesktopAutoPlayNextAction, setupAutoPlayNextAction } from '@/modules/actions/desktop/setupAutoPlayNextAction';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { normalizeSquareContentIndex } from '@/helpers/squareContentIndex';
import { setupVideoIntersectionObserver } from '@/modules/parts/icons';
import { pauseImageTimer, resumeImageTimer } from '@/helpers/mediaUtils';
import { isReelLike } from '@/helpers/isReelLike';
import {
  getPendingQuestionnaireGroupsForContents,
  mergeTargetingAnswers,
  normalizeTargetingAnswers,
  prepareCampaignForDisplay,
  restoreTargetingAnswersFromStorage,
  serializeTargetingAnswersForStorage,
} from '@/helpers/streamsTargeting';


const setQuestionnairePlaybackLock = (isLocked) => {
  window.__storeeelQuestionnairePlaybackLocked = isLocked === true;
};

const resolveCurrentContent = (campaign) => {
  if (!campaign) return null;
  const groups = campaign.contentGroups || [];

  if (isReelLike(campaign.frame)) {
    if (campaign.isMobile) {
      const groupId = String(window.iconId || '').split('-').pop();
      const targetGroup = groups.find((group) => String(group?.id) === String(groupId)) || groups[0];
      return targetGroup?.contents?.[0] || null;
    }
    const allContents = groups.flatMap((group) => group?.contents || []);
    return allContents[Number(window.moviePosition || 0)] || null;
  }

  const groupId = String(window.iconId || '').split('-').pop();
  const targetGroup = groups.find((group) => String(group?.id) === String(groupId)) || groups[0];
  return targetGroup?.contents?.[Number(window.moviePosition || 0)] || null;
};

const resolveCurrentGroup = (campaign) => {
  if (!campaign) return null;

  const groupId = String(window.iconId || '').split('-').pop();
  return (campaign.contentGroups || []).find((group) => String(group?.id) === String(groupId)) || campaign.contentGroups?.[0] || null;
};

const resolveQuestionnaireTriggerContents = (campaign) => {
  if (!campaign) return [];

  if (isReelLike(campaign.frame)) {
    // リール型・タイル型は選択中コンテンツに関係なく、キャンペーン内に広告があれば先にアンケートを出す
    return (campaign.contentGroups || []).flatMap((group) => group?.contents || []);
  }

  if (!campaign.isMobile && campaign.frame === 'circle') {
    // PC のストーリー型は、同じグループ内に広告が含まれていれば即時アンケートを出す
    return resolveCurrentGroup(campaign)?.contents || [];
  }

  const currentContent = resolveCurrentContent(campaign);
  return currentContent ? [currentContent] : [];
};

const resolveQuestionnaireScopeContents = (campaign) => {
  if (!campaign) return [];

  if (isReelLike(campaign.frame) || campaign.frame === 'circle') {
    // リール型・タイル型・ストーリー型は、キャンペーン内の未回答アンケートをまとめて表示する。
    return (campaign.contentGroups || []).flatMap((group) => group?.contents || []);
  }

  const currentContent = resolveCurrentContent(campaign);
  return currentContent ? [currentContent] : [];
};

const prepareModalForQuestionnaire = () => {
  const modal = document.querySelector('.storeeel-modal');
  const groupWrapper = modal?.querySelector('.storeeel-swiper-group-wrapper');
  const previousVisibility = groupWrapper?.style?.visibility ?? '';
  const previousOpacity = groupWrapper?.style?.opacity ?? '';
  const previousPointerEvents = groupWrapper?.style?.pointerEvents ?? '';
  const remainingImageTime = pauseImageTimer();

  // アンケート表示中は裏のコンテンツが再生再開しないように明示的にロックする。
  setQuestionnairePlaybackLock(true);
  if (!window.storeeelItems?.isMobile) {
    // PCでは自動送り/末尾クローズ監視も止めないと、裏で進行してしまう。
    pauseDesktopAutoPlayNextAction();
  }
  pauseAllProgressBars();
  try {
    modal?.querySelectorAll('video').forEach((videoEl) => {
      videoEl.pause?.();
    });
  } catch {
    void 0;
  }

  if (groupWrapper) {
    // Swiper の current translate を維持するため、display:none ではなく可視性だけを切る
    groupWrapper.style.visibility = 'hidden';
    groupWrapper.style.opacity = '0';
    groupWrapper.style.pointerEvents = 'none';
  }

  return () => {
    setQuestionnairePlaybackLock(false);
    if (groupWrapper) {
      groupWrapper.style.visibility = previousVisibility;
      groupWrapper.style.opacity = previousOpacity;
      groupWrapper.style.pointerEvents = previousPointerEvents;
    }
    resumeAllProgressBars();
    // アンケート表示中に画像の自動送りが進まないよう、残り時間から再開する。
    resumeImageTimer(undefined, remainingImageTime);
    if (!window.storeeelItems?.isMobile && modal?.querySelector('.swiper-slide-active .storeeel-movie')) {
      setupAutoPlayNextAction();
    }
    activeVideoPlay();
  };
};

const reopenModalWithLatestAnswers = async () => {
  const module = await import('@/modules/setupVideoPlayerModal');
  module.setupVideoPlayerModal();
};

const refreshOtherPcSquareCampaignDisplays = (originalCampaign, answers, options = {}) => {
  const campaigns = Array.isArray(window.storeeelData) ? window.storeeelData : [];

  return campaigns.reduce((result, campaign) => {
    if (!campaign) return result;
    if (String(campaign?.id) === String(originalCampaign?.id)) return result;
    if (!isReelLike(campaign?.frame) || campaign?.isMobile) return result;

    // 別キャンペーンのPCリール型・タイル型も、回答後の閉じた見た目を実際に開く内容へ合わせる。
    const preparedCampaign = prepareCampaignForDisplay(campaign, answers, options);
    const refreshResult = refreshPreparedCampaignThumbnailDisplay(campaign, preparedCampaign, answers);

    return {
      updated: Boolean(result.updated || refreshResult?.updated),
      created: Boolean(result.created || refreshResult?.created),
    };
  }, { updated: false, created: false });
};

const bindCampaignIconsIfNeeded = async () => {
  const { campaignIconsTapAction } = await import('@/modules/actions/campaignIconsTapAction');
  campaignIconsTapAction();
};

const captureQuestionnaireResumePosition = (campaign) => {
  const normalizedIconId = String(window.iconId || '').split('-').pop();
  const moviePosition = Number(window.moviePosition || 0);

  if (!(campaign?.isMobile && isReelLike(campaign?.frame))) {
    return {
      iconId: normalizedIconId,
      moviePosition,
    };
  }

  const swiper = getSwiperInstance();
  const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
  const activeWrapper = activeSlide?.querySelector('.slide-media-wrapper');
  const groupId = String(activeWrapper?.getAttribute?.('group-id') || normalizedIconId || '');
  const slideIndexAttr = Number(activeSlide?.getAttribute?.('data-swiper-slide-index'));
  const fallbackIndex = campaign.contentGroups.findIndex((group) => String(group?.id) === groupId);
  const realIndex = Number.isFinite(slideIndexAttr)
    ? normalizeSquareContentIndex(slideIndexAttr, campaign)
    : (Number.isFinite(swiper?.realIndex)
      ? normalizeSquareContentIndex(swiper.realIndex, campaign)
      : Math.max(0, fallbackIndex));

  return {
    iconId: groupId,
    moviePosition: 0,
    realIndex,
  };
};

const restoreQuestionnaireResumePosition = (campaign, resumePosition) => {
  if (!resumePosition) return;

  if (resumePosition.iconId) {
    window.iconId = String(resumePosition.iconId);
  }
  if (Number.isFinite(resumePosition.moviePosition)) {
    window.moviePosition = resumePosition.moviePosition;
  }

  if (!(campaign?.isMobile && isReelLike(campaign?.frame))) return;
  if (!Number.isFinite(resumePosition.realIndex)) return;

  const swiper = getSwiperInstance();
  const currentRealIndex = Number.isFinite(swiper?.realIndex)
    ? normalizeSquareContentIndex(swiper.realIndex, campaign)
    : null;
  if (currentRealIndex === resumePosition.realIndex) return;

  if (typeof swiper?.slideToLoop === 'function') {
    swiper.slideToLoop(resumePosition.realIndex, 0);
  } else if (typeof swiper?.slideTo === 'function') {
    swiper.slideTo(resumePosition.realIndex, 0);
  }
};

const hasAnsweredQuestionnaireGroups = (questionnaireGroups, answers) => (
  questionnaireGroups.some((group) => {
    const segmentKeyId = String(group?.segmentKeyId || '');
    return segmentKeyId && Boolean(answers?.segments?.[segmentKeyId]);
  })
);

const hasUnansweredQuestionnaireGroups = (questionnaireGroups, answers) => (
  questionnaireGroups.some((group) => {
    const segmentKeyId = String(group?.segmentKeyId || '');
    return segmentKeyId && !answers?.segments?.[segmentKeyId];
  })
);

const normalizeResumePosition = (campaign, resumePosition, fallbackPosition) => {
  if (!resumePosition) return fallbackPosition;

  return {
    iconId: String(resumePosition.iconId ?? fallbackPosition?.iconId ?? '').split('-').pop(),
    moviePosition: Number.isFinite(Number(resumePosition.moviePosition))
      ? Number(resumePosition.moviePosition)
      : (fallbackPosition?.moviePosition ?? 0),
    realIndex: Number.isFinite(Number(resumePosition.realIndex))
      ? Number(resumePosition.realIndex)
      : fallbackPosition?.realIndex,
  };
};

const maybeShowTargetingQuestionnaire = ({
  triggerContents,
  completeResumePosition,
  cancelResumePosition,
  onContinueWithoutAnswers,
} = {}) => {
  if (document.querySelector('[data-storeeel-questionnaire-modal="true"]')) return true;

  const originalCampaign = window.storeeelOriginalItems || window.storeeelItems;
  if (!originalCampaign) return false;

  const tenantId = window.storeeelTenantId || 'default';
  const currentUrl = window.storeeelCurrentUrl || window.location.href;
  const savedAnswers = window.storeeelTargetingAnswers
    ? normalizeTargetingAnswers(window.storeeelTargetingAnswers)
    : restoreTargetingAnswersFromStorage(originalCampaign, {
      segments: localStorageRepositories.get('targetingSegment')?.get?.(tenantId) || {},
    });
  const questionnaireTriggerContents = Array.isArray(triggerContents) && triggerContents.length > 0
    ? triggerContents
    : resolveQuestionnaireTriggerContents(originalCampaign);
  const triggerQuestionnaireGroups = getPendingQuestionnaireGroupsForContents(originalCampaign, questionnaireTriggerContents, savedAnswers);
  if (triggerQuestionnaireGroups.length === 0) return false;

  const questionnaireScopeContents = resolveQuestionnaireScopeContents(originalCampaign);
  const questionnaireGroups = getPendingQuestionnaireGroupsForContents(originalCampaign, questionnaireScopeContents, savedAnswers);

  if (questionnaireGroups.length === 0) return false;
  if (localStorageRepositories.get('targetingQuestionnaireSkip')?.get?.(tenantId, originalCampaign.id)) {
    // 未回答で閉じた直後は1週間だけ再表示しない
    return false;
  }

  const restoreModalContent = prepareModalForQuestionnaire();
  const currentResumePosition = captureQuestionnaireResumePosition(originalCampaign);
  const completedResumePosition = normalizeResumePosition(originalCampaign, completeResumePosition, currentResumePosition);
  const cancelledResumePosition = normalizeResumePosition(originalCampaign, cancelResumePosition, currentResumePosition);
  const continueWithoutAnswers = () => {
    if (typeof onContinueWithoutAnswers !== 'function') return;
    window.setTimeout(() => {
      onContinueWithoutAnswers();
    }, 0);
  };
  const markQuestionnaireSkipped = () => {
    try {
      // 設問をひとつも選ばず完了した場合も、明示的なスキップと同等に扱う。
      localStorageRepositories.get('targetingQuestionnaireSkip')?.set?.(tenantId, originalCampaign.id);
    } catch (e) {
      console.warn('localStorageのtargetingQuestionnaireSkipの保存に失敗:', e);
    }
  };

  showQuestionnaireModal({
    questionnaireGroups,
    initialAnswers: savedAnswers,
    onComplete: (answers) => {
      const mergedAnswers = mergeTargetingAnswers(savedAnswers, answers);
      if (!hasAnsweredQuestionnaireGroups(questionnaireGroups, mergedAnswers)) {
        markQuestionnaireSkipped();
        restoreQuestionnaireResumePosition(originalCampaign, cancelledResumePosition);
        restoreModalContent();
        continueWithoutAnswers();
        return;
      }
      if (hasUnansweredQuestionnaireGroups(questionnaireGroups, mergedAnswers)) {
        // 一部だけ回答して完了した場合も、未回答設問はスキップと同等に再表示を抑止する。
        markQuestionnaireSkipped();
      }

      const storedAnswers = serializeTargetingAnswersForStorage(originalCampaign, mergedAnswers);
      try {
        // アンケートごとの resetDays を反映できるよう、設問単位で保存する
        localStorageRepositories.get('targetingSegment')?.set?.(tenantId, storedAnswers.segments, originalCampaign.questionnaireGroups);
      } catch (e) {
        console.warn('localStorageのtargetingSegmentの保存に失敗:', e);
      }
      window.storeeelTargetingAnswers = mergedAnswers;
      window.storeeelItems = prepareCampaignForDisplay(originalCampaign, mergedAnswers, {
        tenantId,
        currentUrl,
        // 回答直後は最新回答を即反映するため、そのキャンペーンの固定割り当てを作り直す
        resetSessionAssignments: true,
        // 再割り当ても視聴再開するグループを起点にし、手前のグループへ広告を吸い取られないようにする
        startGroupId: completedResumePosition?.iconId,
      });
      const refreshResult = refreshPreparedCampaignThumbnailDisplay(originalCampaign, window.storeeelItems, mergedAnswers);
      const relatedRefreshResult = refreshOtherPcSquareCampaignDisplays(originalCampaign, mergedAnswers, {
        tenantId,
        currentUrl,
      });
      if (refreshResult?.updated || relatedRefreshResult?.updated) {
        setupVideoIntersectionObserver();
        if (refreshResult?.created || relatedRefreshResult?.created) {
          void bindCampaignIconsIfNeeded();
        }
      }
      window.__storeeelQuestionnaireResumePosition = completedResumePosition;
      restoreQuestionnaireResumePosition(originalCampaign, completedResumePosition);
      setQuestionnairePlaybackLock(false);
      closeModal();
      window.setTimeout(() => {
        void reopenModalWithLatestAnswers();
      }, 0);
    },
    onCancel: () => {
      markQuestionnaireSkipped();
      restoreQuestionnaireResumePosition(originalCampaign, cancelledResumePosition);
      restoreModalContent();
      continueWithoutAnswers();
    },
  });

  return true;
};

const resolveCurrentOriginalContentPosition = (campaign) => {
  const fallbackPosition = captureQuestionnaireResumePosition(campaign);
  const groupId = String(fallbackPosition?.iconId || '').split('-').pop();
  const currentGroup = (campaign?.contentGroups || []).find((group) => String(group?.id) === groupId) || null;
  if (!currentGroup) return null;

  const activeContentId = document
    .querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active [content-id]')
    ?.getAttribute('content-id');
  const currentIndexFromContentId = activeContentId
    ? currentGroup.contents.findIndex((content) => String(content?.id) === String(activeContentId))
    : -1;
  const currentIndex = currentIndexFromContentId >= 0
    ? currentIndexFromContentId
    : Number(fallbackPosition?.moviePosition || 0);

  return {
    iconId: groupId,
    moviePosition: currentIndex,
  };
};

export const maybeShowTargetingQuestionnaireForNextContent = ({ onContinueWithoutAnswers } = {}) => {
  const originalCampaign = window.storeeelOriginalItems || window.storeeelItems;
  if (!originalCampaign) return false;

  const currentPosition = resolveCurrentOriginalContentPosition(originalCampaign);
  if (!currentPosition) return false;

  const currentGroup = (originalCampaign.contentGroups || []).find((group) => String(group?.id) === String(currentPosition.iconId)) || null;
  const nextMoviePosition = Number(currentPosition.moviePosition) + 1;
  const nextContent = currentGroup?.contents?.[nextMoviePosition] || null;
  if (!nextContent) return false;

  return maybeShowTargetingQuestionnaire({
    triggerContents: [nextContent],
    completeResumePosition: {
      iconId: currentPosition.iconId,
      moviePosition: nextMoviePosition,
    },
    cancelResumePosition: currentPosition,
    onContinueWithoutAnswers,
  });
};

export const maybeShowTargetingQuestionnaireForCurrentContent = () => maybeShowTargetingQuestionnaire();
